import React, { useState, createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { Report } from "../../model/reports";
import { useAppContext } from "../../libs/contextLib";
import { ulid } from "ulid";
import dayjs from "../../dayjs";
import { reportsModelData, routesModelExpiryService } from "../../services/ModelService";
import { s3Upload } from "../../libs/awsLib";
import { Storage } from 'aws-amplify';
import Papa from 'papaparse';

const DEFAULT_FILTERS = {
  dateRange: {
    start: dayjs().startOf("month"),
    end: dayjs().endOf("month")
  },
  reportType: null,
  searchTerm: "",
  routes: [],
  drivers: [],
  depots: [],
  operatorIds: [],
  contracts: [],
  timePeriod: null
}

// Create the Context
const ReportsContext = createContext();

// Provider Component
const ReportsProvider = ({ children }) => {
  const { messageApi, operator, email } = useAppContext();
  
  // State for reports
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filters, setFilters] = useState({...DEFAULT_FILTERS});

  // Fetch reports from the API
  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedReports = await reportsModelData.getAll({ asArray: true, apiKey: operator?.operatorId });
      setReports(fetchedReports || []);
    } catch (error) {
      messageApi?.error("Failed to fetch reports");
      console.error("Error fetching reports:", error);
      // Always ensure reports is an array even on error
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  }, [messageApi, operator?.operatorId]);

  // Initial load
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Filter reports based on current filters
  const filteredReports = useMemo(() => {
    // Ensure reports is an array before filtering
    const reportsArray = Array.isArray(reports) ? reports : [];
    
    return reportsArray.filter(report => {
      // Apply search term filter
      if (filters.searchTerm && !report.reportName.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
        return false;
      }
      
      // Apply report type filter
      if (filters.reportType && report.reportType !== filters.reportType) {
        return false;
      }
      
      // Apply date range filter if there's a reportDate
      if (filters.dateRange?.start && filters.dateRange?.end && report.reportDate) {
        const reportDate = dayjs(report.reportDate);
        const start = dayjs(filters.dateRange.start);
        const end = dayjs(filters.dateRange.end);
        
        if (!reportDate.isValid() || !reportDate.isBetween(start, end, null, '[]')) {
          return false;
        }
      }
      
      return true;
    });
  }, [reports, filters]);

  // Upload CSV file and create a report
  const uploadReport = useCallback(async (file, reportDate, reportName, reportType = 'otr', fileType = 'csv') => {
    setIsLoading(true);
    setUploadProgress(0);
    
    try {
      // Format date for comparison and filename
      const formattedDate = dayjs(reportDate).format('YYYY-MM-DD');
      
      // Check if a report already exists for this date and type
      const existingReport = Array.isArray(reports) ? 
        reports.find(report => 
          report.reportType === reportType &&
          dayjs(report.reportDate).format('YYYY-MM-DD') === formattedDate
        ) : null;
      
      // Generate a report ID (use existing ID if replacing)
      const reportId = existingReport ? existingReport.reportId : ulid();
      
      // Create a filename based on date and original filename
      const fileName = `${formattedDate}_${file.name}`;
      const s3Key = `reports/${operator?.operatorId || 'public'}/${reportType}/${fileName}`;
      
      // Upload to S3
      const fileKey = await s3Upload(file, s3Key, {
        level: 'private', 
        progressCallback: (progress) => {
          const percentage = Math.round((progress.loaded / progress.total) * 100);
          setUploadProgress(percentage);
        }
      });

      // Parse CSV - correctly handling the header row
      const parseOptions = {
        delimiter: ";", // Using semicolon as delimiter for standard OTR format
        header: true,  // First row is the header
        skipEmptyLines: true,
        transformHeader: h => h.trim(),
        transform: v => v.trim()
      };
      
      const parsedData = Papa.parse(file, parseOptions);

      // array of all unique dates in the SCHEDULED_CALENDAR_DATE column
      const dates = [...new Set(parsedData.map(row => row.SCHEDULED_CALENDAR_DATE))];
      console.log("Dates", dates);

      
      // Create a new report object or update existing one
      const reportData = {
        reportId,
        reportName: reportName || `${reportType.toUpperCase()} Report - ${formattedDate}`,
        reportDescription: `${reportType.toUpperCase()} report for ${formattedDate}`,
        reportType,
        updatedAt: new Date().toISOString(),
        uploadedBy: email,
        reportDates: dates,
        reportDate: reportDate.toISOString(),
        fileName: file.name,
        fileSize: file.size,
        fileType,
        fileKey,
        status: 'active',
        companyId: operator?.companyId
      };
      
      if (!existingReport) {
        // This is a new report
        reportData.createdAt = new Date().toISOString();
        reportData.createdBy = email;
      } else {
        // Preserve creation info when replacing
        reportData.createdAt = existingReport.createdAt;
        reportData.createdBy = existingReport.createdBy;
      }
      
      const report = new Report(reportData);
      
      // Save to database using the model data
      await reportsModelData.save(report);
      
      // Update the local state
      await fetchReports();
      
      if (existingReport) {
        messageApi?.success("Existing report has been replaced");
      } else {
        messageApi?.success("Report uploaded successfully");
      }
      
      return report;
    } catch (error) {
      messageApi?.error("Failed to upload report");
      console.error("Error uploading report:", error);
      return null;
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  }, [messageApi, fetchReports, email, operator, reports]);

  // Delete a report
  const deleteReport = useCallback(async (reportId) => {
    setIsLoading(true);
    
    try {
      // Delete from database
      await reportsModelData.delete(reportId, true);
      
      // If the deleted report was selected, clear selection
      if (selectedReport?.reportId === reportId) {
        setSelectedReport(null);
      }
      
      // Remove from local state
      setReports(prev => Array.isArray(prev) ? prev.filter(report => report.reportId !== reportId) : []);
      
      messageApi?.success("Report deleted successfully");
    } catch (error) {
      messageApi?.error("Failed to delete report");
      console.error("Error deleting report:", error);
    } finally {
      setIsLoading(false);
    }
  }, [messageApi, selectedReport]);

  const getReportUrl = useCallback(async (report) => {
    let key = report.fileKey
    if(report.fileKey.startsWith('private/')) {
      key = report.fileKey.replace('private/', '');
    }
    return await Storage.get(key, {
      level: 'public',
      customPrefix: {
        public: 'private/'
      },
      completeCallback: (event) => {
          console.log(`Successfully uploaded ${key}`);
      },
      progressCallback: (progress) => {
          console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
      },
      errorCallback: (err) => {
          console.error('Unexpected error while uploading', err);
      },
      expires: 3600 // 1 hour expiration
    });
  }, []);

  // Download a report
  const downloadReport = useCallback(async (report) => {
    if (!report || !report.fileKey) {
      messageApi?.error("No file available for download");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get the download URL
      const url = await getReportUrl(report);
      
      // Download the file
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Save to local database
      const fileId = ulid();
      const downloadedFile = {
        fileId,
        reportId: report.reportId,
        fileName: report.fileName,
        fileType: report.fileType,
        fileSize: report.fileSize,
        downloadedAt: new Date().toISOString(),
        blob: blob
      };
      
      await routesModelExpiryService.db.downloadedFiles.add(downloadedFile);
      report.reportData = downloadedFile;
      
      // Open the file in a new tab
      window.open(url, '_blank');
      
      messageApi?.success("Report downloaded and saved locally");
    } catch (error) {
      messageApi?.error("Failed to download report");
      console.error("Error downloading report:", error);
    }
    setIsLoading(false);
  }, [messageApi, getReportUrl]);

  // Report types for OTR
  const reportTypes = [
    { id: 'otr', name: 'On-Time Running' },
    { id: 'operational', name: 'Operational Reports' },
    { id: 'compliance', name: 'Compliance Reports' }
  ];

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    if(typeof newFilters === 'function') {
      setFilters(prev => ({
        ...prev,
        ...newFilters(prev)
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        ...newFilters
      }));
    }
  }, [setFilters]);

  const setDateRange = useCallback((range) => {
    if (Array.isArray(range) && range.length === 2 && range[1]) {
      range = [range[0], range[1].endOf('day')];
    }
    updateFilters({
      dateRange: range
    });
  }, [updateFilters]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({...DEFAULT_FILTERS});
  }, []);

  const getReportData = useCallback(async () => {
    return await Promise.all(
      filteredReports.map(async (report) => {
        try {
          // First check if we have the file in local storage
          const downloadedFile = report.reportData;

          if (downloadedFile) {
            console.log(`Found report ${report.reportId} in local storage`);
            // Convert blob to text
            const text = await downloadedFile.blob.text();
            return { report, data: text };
          }

          // If not in local storage, fetch from server
          console.log(`Fetching data for report ${report.reportId} from server`);
          const url = await getReportUrl(report);
          const response = await fetch(url);
          const blob = await response.blob();
          const text = await blob.text();

          // Save to local database
          const fileId = ulid();
          const downloadedFileData = {
            fileId,
            reportId: report.reportId,
            fileName: report.fileName,
            fileType: report.fileType,
            fileSize: report.fileSize,
            downloadedAt: new Date().toISOString(),
            blob: blob
          };

          await routesModelExpiryService.db.downloadedFiles.add(downloadedFileData);
          console.log(`Saved report ${report.reportId} to local storage`);
          report.reportData = downloadedFileData;

          return { report, data: text };
        } catch (error) {
          console.error(`Error fetching report ${report.reportId}:`, error);
          return null;
        }
      })
    );
  }, [filteredReports, getReportUrl]);

  // Context value
  const contextValue = {
    reports: Array.isArray(reports) ? reports : [],
    filteredReports,
    selectedReport,
    setSelectedReport,
    isLoading,
    uploadProgress,
    filters,
    updateFilters,
    clearFilters,
    uploadReport,
    deleteReport,
    downloadReport,
    fetchReports,
    setDateRange,
    reportTypes,
    getReportData,
    getReportUrl,
  };

  return (
    <ReportsContext.Provider value={contextValue}>
      {children}
    </ReportsContext.Provider>
  );
};

// Consumer Hook (for functional components)
const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReports must be used within a ReportsProvider");
  }
  return context;
};

// Export both the context, provider, and hook
export { ReportsContext, ReportsProvider, useReports }; 
